import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Megaphone, ArrowLeft, Loader2, Check, Disc3, ImagePlus, X as XIcon, Send } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { guestGet, guestSet } from '../lib/guestStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Venue-scoped guest shoutout composer — reachable from the permanent QR
 * landing page (`/v/:venueCode/shoutout`). Unlike the commercial session-scoped
 * composer inside GuestRequestPage, this one posts to the HOST-scoped endpoint
 * (`POST /venue/:venueCode/shoutout`) so it works for AI venues that never run
 * a live request session. Staff approve it from the AI/commercial shoutout
 * inbox before it appears on screen.
 */
const GuestShoutoutPage = () => {
  const { venueCode } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const VENUE_KEY = `guest_venue_${venueCode}`;
  const stored = guestGet(VENUE_KEY);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [guestName, setGuestName] = useState(stored?.name || guestGet('guestName') || '');
  const [message, setMessage] = useState('');
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/venue/${venueCode}/info`);
        setInfo(r.data);
      } catch (err) {
        setError(err?.response?.status === 404 ? 'Venue not found' : 'Could not load venue');
      } finally {
        setLoading(false);
      }
    })();
  }, [venueCode]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image file'); return; }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onerror = reject;
        im.onload = () => resolve(im);
        im.src = dataUrl;
      });
      const maxW = 800;
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL('image/jpeg', 0.75);
      if (out.length > 250_000) { toast.error('Photo too large even after resize — try another'); return; }
      setPhoto(out);
    } catch (photoErr) {
      toast.error("Couldn't read that photo, try another");
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const name = guestName.trim();
    if (!name) { toast.error('Add your name first'); return; }
    if (!message.trim()) { toast.error('Type a message first'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/venue/${venueCode}/shoutout`, {
        guest_name: name,
        message: message.trim(),
        photo_data_url: photo || undefined,
      });
      // Remember the guest's name so they aren't re-prompted next time.
      guestSet('guestName', name);
      guestSet(VENUE_KEY, { ...(stored || {}), name, at: new Date().toISOString() });
      setSent(true);
      setMessage('');
      setPhoto(null);
      toast.success('Shoutout sent — the venue will see it shortly');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send shoutout');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
        <Disc3 className="w-12 h-12 text-gray-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">{error || 'Venue not found'}</h1>
        <p className="text-gray-500 text-sm">Double-check the QR code or ask the venue staff.</p>
      </div>
    );
  }

  const { venue_name, venue_logo_url, host_features = {} } = info;
  const shoutoutsOn = host_features.shoutouts !== false;

  if (!shoutoutsOn) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center" data-testid="guest-shoutout-disabled">
        <Megaphone className="w-12 h-12 text-gray-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">Shoutouts are off</h1>
        <p className="text-gray-500 text-sm mb-6">{venue_name} isn&apos;t taking shoutouts right now.</p>
        <button
          onClick={() => navigate(`/v/${venueCode}`)}
          className="text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-white font-['JetBrains_Mono']"
          data-testid="guest-shoutout-disabled-back"
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="guest-shoutout-page">
      <Toaster position="top-center" />
      <header className="px-4 py-4 border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(`/v/${venueCode}`)}
            className="text-gray-400 hover:text-white"
            data-testid="guest-shoutout-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm font-['Outfit']">Send a Shoutout</p>
            <p className="text-gray-500 text-xs">{venue_name}</p>
          </div>
          {venue_logo_url ? (
            <img src={venue_logo_url} alt={venue_name} className="w-9 h-9 rounded-lg object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
              <Megaphone className="w-4 h-4 text-[#9D4CDD]" />
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 w-full max-w-lg mx-auto px-5 py-8">
        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/[0.04] border border-[#39FF14]/30 rounded-3xl p-8 text-center"
            data-testid="guest-shoutout-success"
          >
            <div className="w-16 h-16 rounded-full bg-[#39FF14]/15 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#39FF14]" />
            </div>
            <h2 className="text-2xl font-black text-white font-['Outfit'] mb-2">Shoutout sent! 🎉</h2>
            <p className="text-gray-400 text-sm mb-6">
              The venue will review it and pop it up on screen shortly.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setSent(false)}
                className="w-full bg-[#9D4CDD] hover:bg-[#8a3ec7] text-white font-bold tracking-wider rounded-full h-12 transition-all"
                data-testid="guest-shoutout-send-another"
              >
                Send another
              </button>
              <button
                onClick={() => navigate(`/v/${venueCode}`)}
                className="w-full text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-white font-['JetBrains_Mono'] py-2"
                data-testid="guest-shoutout-done"
              >
                ← Back to menu
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSend}
            className="bg-white/[0.04] border border-white/12 rounded-3xl p-5 backdrop-blur-xl"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Megaphone className="w-4 h-4 text-[#9D4CDD]" />
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#9D4CDD] font-['JetBrains_Mono'] font-bold">
                Surprise your crew
              </p>
            </div>
            <h2 className="text-xl font-bold text-white font-['Outfit'] mb-5">Put a message on the screen</h2>

            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] font-bold mb-1.5">
              Your name
            </label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value.slice(0, 40))}
              placeholder="Your first name"
              className="w-full bg-black/40 border border-white/10 text-white text-base rounded-xl px-3 py-3 mb-4 outline-none focus:border-[#9D4CDD]/50 placeholder:text-gray-600"
              data-testid="guest-shoutout-name"
            />

            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] font-bold mb-1.5">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 240))}
              placeholder="Happy Birthday Priya! 🎂 The whole crew's cheering for you!"
              rows={3}
              className="w-full bg-black/40 border border-white/10 text-white rounded-xl p-3 text-sm resize-none outline-none focus:border-[#9D4CDD]/50 placeholder:text-gray-600"
              data-testid="guest-shoutout-message"
            />
            <div className="flex items-center justify-between mt-1 mb-4">
              <span className="text-[10px] text-gray-600 font-mono">{message.length}/240</span>
            </div>

            {/* Optional photo */}
            {photo ? (
              <div className="relative mb-4">
                <img src={photo} alt="attachment" className="w-full rounded-2xl object-cover max-h-56" />
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center"
                  data-testid="guest-shoutout-photo-remove"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 p-3 mb-4 rounded-xl border border-dashed border-white/10 hover:border-[#9D4CDD]/40 text-gray-400 hover:text-white text-xs transition-colors"
                data-testid="guest-shoutout-photo-add"
              >
                <ImagePlus className="w-4 h-4" /> Add a photo (optional)
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />

            <button
              type="submit"
              disabled={sending || !guestName.trim() || !message.trim()}
              className="w-full bg-[#9D4CDD] hover:bg-[#8a3ec7] disabled:bg-white/10 disabled:text-gray-600 text-white font-bold tracking-wider rounded-full h-12 flex items-center justify-center gap-2 transition-all"
              data-testid="guest-shoutout-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              SEND TO SCREEN
            </button>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default GuestShoutoutPage;
